import { toast } from 'sonner'
import {
  type ProjectRoleDefault,
  type ProjectRoleDefaultField,
  useProjectRoleDefaults,
  useUpdateProjectRoleDefaults,
} from '@/api/hooks'
import { BackButton } from '@/components/back-button'
import {
  StaffAssignmentPicker,
  type StaffAssignmentUser,
} from '@/components/staff-assignment-picker'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { StaffFunctionOwners } from '@/features/staff-functions/staff-function-owners'

const PROJECT_TYPES = [
  { value: 'shopify', label: 'Shopify' },
  { value: 'dropship', label: 'Dropship' },
] as const

const ROLES: Array<{
  label: string
  field: ProjectRoleDefaultField
  userField: 'sales_person' | 'ops_person' | 'warehousing_person' | 'accounting_person'
}> = [
  { label: 'Sales', field: 'sales_person_id', userField: 'sales_person' },
  { label: 'Operations', field: 'ops_person_id', userField: 'ops_person' },
  {
    label: 'Warehousing',
    field: 'warehousing_person_id',
    userField: 'warehousing_person',
  },
  {
    label: 'Accounting',
    field: 'accounting_person_id',
    userField: 'accounting_person',
  },
]

function updateBody(
  defaults: ProjectRoleDefault,
  field: ProjectRoleDefaultField,
  userId: number | null,
) {
  return {
    sales_person_id: defaults.sales_person_id ?? null,
    ops_person_id: defaults.ops_person_id ?? null,
    warehousing_person_id: defaults.warehousing_person_id ?? null,
    accounting_person_id: defaults.accounting_person_id ?? null,
    [field]: userId,
  }
}

export function ProjectRoleDefaultsPage() {
  const defaultsQuery = useProjectRoleDefaults()
  const updateDefaults = useUpdateProjectRoleDefaults()

  const changeAssignment = (
    defaults: ProjectRoleDefault,
    field: ProjectRoleDefaultField,
    roleLabel: string,
    projectLabel: string,
    user: StaffAssignmentUser | null,
  ) => {
    updateDefaults.mutate(
      {
        projectType: defaults.project_type,
        field,
        body: updateBody(defaults, field, user?.id ?? null),
      },
      {
        onSuccess: () => toast.success(`${projectLabel} ${roleLabel} updated`),
        onError: () => toast.error(`Could not update ${projectLabel} ${roleLabel}`),
      },
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <BackButton fallback={{ to: '/staff' }} fallbackLabel="Back to Staff" />

      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Project role defaults</h1>
        <p className="text-sm text-muted-foreground">
          Applied when new Shopify and dropship projects are created.
        </p>
      </div>

      {defaultsQuery.isLoading ? (
        <div className="overflow-hidden rounded-xl border bg-card">
          <div className="hidden grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)] gap-6 border-b bg-muted/40 px-5 py-3 lg:grid">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="divide-y">
            {ROLES.map((role) => (
              <div
                key={role.field}
                className="grid gap-4 px-5 py-4 lg:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)] lg:items-center lg:gap-6"
              >
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-9 w-full max-w-64 rounded-full" />
                <Skeleton className="h-9 w-full max-w-64 rounded-full" />
              </div>
            ))}
          </div>
        </div>
      ) : defaultsQuery.isError ? (
        <div className="flex flex-col items-start gap-3 rounded-xl border p-5">
          <p className="text-sm font-medium">Project role defaults could not be loaded.</p>
          <Button variant="outline" size="sm" onClick={() => defaultsQuery.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <div className="hidden grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)] gap-6 border-b bg-muted/40 px-5 py-3 text-sm font-medium lg:grid">
            <span>Role</span>
            {PROJECT_TYPES.map((projectType) => (
              <span key={projectType.value}>{projectType.label}</span>
            ))}
          </div>

          <div className="divide-y">
            {ROLES.map((role) => (
              <div
                key={role.field}
                className="grid gap-4 px-5 py-4 lg:grid-cols-[10rem_minmax(0,1fr)_minmax(0,1fr)] lg:items-center lg:gap-6"
              >
                <h2 className="text-sm font-semibold">{role.label}</h2>
                {PROJECT_TYPES.map((projectType) => {
                  const defaults = defaultsQuery.data?.find(
                    (item) => item.project_type === projectType.value,
                  )
                  const pending =
                    updateDefaults.isPending &&
                    updateDefaults.variables?.projectType === projectType.value &&
                    updateDefaults.variables.field === role.field

                  return (
                    <div key={projectType.value} className="min-w-0 space-y-2">
                      <p className="text-xs font-medium text-muted-foreground lg:hidden">
                        {projectType.label}
                      </p>
                      {defaults ? (
                        <StaffAssignmentPicker
                          label={role.label}
                          currentUser={defaults[role.userField]}
                          disabled={updateDefaults.isPending}
                          pending={pending}
                          showName
                          onChange={(user) =>
                            changeAssignment(
                              defaults,
                              role.field,
                              role.label,
                              projectType.label,
                              user,
                            )
                          }
                          className="w-full max-w-none justify-start"
                        />
                      ) : (
                        <p className="text-sm text-destructive">Defaults missing</p>
                      )}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      <StaffFunctionOwners />
    </div>
  )
}
